import PDFMake from 'pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { getFooter, nestedTableStyling, formatAddress } from './GenericSettlementsFunctions.js'
import { formatMoney, tonStringFromPounds, mbfStringFromBf } from '@/utils/NumericMutations.js'
import { DeckStatus, ConsumptionMode } from '@/utils/Enumerations.js'
import store from '@/store/index.js'
import colors from 'vuetify/lib/util/colors'
import moment from 'moment'
import { sumBy } from 'lodash'

PDFMake.vfs = pdfFonts.pdfMake.vfs

const SIDE_MARGIN = 30
const noBorder = [false, false, false, false]
const bottomBorder = [false, false, false, true]
const topBorder = [false, true, false, false]
const rowColor = (idx) => idx % 2 === 0 ? colors.grey.lighten3 : null

export const generateDeckInventoryPDF = (decks, locationName, asOfDate) => {
  const docDefinition = {
    header: getHeader(locationName, asOfDate),
    footer: (page, pages) => getFooter(page, pages, false),
    pageMargins: [SIDE_MARGIN, 100, SIDE_MARGIN, 30],
    pageOrientation: 'landscape',
    headerRows: 1,
    styles: {
      header: {
        fontSize: 15
      },
      rightAlign: {
        alignment: 'right'
      },
      nestedTable: {
        fontSize: 8
      }
    },
    content: [
      getDeckTable(decks)
    ],
    style: 'nestedTable'
  }

  const isArchived = asOfDate && !moment(asOfDate).isSame(moment(), 'day')
  const filename = isArchived
    ? `${locationName}_Decks_${moment.utc(asOfDate).format('YYYYMMDD')}.pdf`
    : `${locationName}_Decks_${moment.utc().format('YYYYMMDD')}.pdf`

  PDFMake.createPdf(docDefinition).download(filename.replace(/\s/g, ''))
}

function getHeader (locationName, asOfDate) {
  const companyInfo = store.getters['user/companyInfo']
  const companyName = companyInfo.name ? companyInfo.name : ''
  const companyAddress = formatAddress(companyInfo)
  const companyPhoneNumber = companyInfo.mainContact && companyInfo.mainContact.phoneNumber ? companyInfo.mainContact.phoneNumber : ''

  const isArchived = asOfDate && !moment(asOfDate).isSame(moment(), 'day')
  const asOfDateText = isArchived ? moment.utc(asOfDate).format('MM/DD/YYYY') : moment.utc().format('MM/DD/YYYY')
  const printedDate = moment.utc().format('MM/DD/YYYY')

  return {
    fontSize: 15,
    bold: true,
    marginTop: 20,
    marginLeft: 35,
    marginRight: 35,
    table: {
      widths: '*',
      body: [
        [
          {
            table: {
              body: [
                [''],
                [{ text: companyName, alignment: 'left' }],
                [{ text: companyAddress, alignment: 'left', fontSize: 8 }],
                [{ text: companyPhoneNumber, alignment: 'left', fontSize: 8 }]
              ]
            },
            layout: 'noBorders'
          },
          {
            alignment: 'center',
            table: {
              widths: '*',
              body: [
                [{ text: locationName, fontSize: 15, alignment: 'center' }],
                [{ text: `Inventory as of ${asOfDateText}`, fontSize: 12, alignment: 'center' }],
                [{ text: `Printed ${printedDate}`, fontSize: 10, alignment: 'center' }]
              ]
            },
            layout: 'noBorders'
          },
          { text: '' }
        ]
      ]
    },
    layout: 'noBorders'
  }
}

function getDeckTable (decks) {
  const headerFontSize = 10
  const bodyFontSize = 8

  const headers = [
    { text: 'Name', bold: true, border: bottomBorder, fontSize: headerFontSize, alignment: 'left' },
    { text: 'Current Status', bold: true, border: bottomBorder, fontSize: headerFontSize, alignment: 'center' },
    { text: 'Consumption Mode', bold: true, border: bottomBorder, fontSize: headerFontSize, alignment: 'center' },
    { text: 'Loads on Deck', bold: true, border: bottomBorder, fontSize: headerFontSize, alignment: 'right' },
    { text: 'On Deck (Tons)', bold: true, border: bottomBorder, fontSize: headerFontSize, alignment: 'right' },
    { text: 'On Deck (MBF)', bold: true, border: bottomBorder, fontSize: headerFontSize, alignment: 'right' },
    { text: 'Value', bold: true, border: bottomBorder, fontSize: headerFontSize, alignment: 'right' }
  ]

  const rows = decks.map((deck, idx) => {
    const statusText = DeckStatus.fromInt(deck.status) || 'Unknown'
    const consumptionModeText = determineConsumptionModeText(deck.consumptionMode)
    const loadsText = deck.consumptionMode === ConsumptionMode.WeightBased.value || deck.consumptionMode === ConsumptionMode.BoardFoot.value ? 'N/A' : (deck.loadsOnDeck || 0).toString()
    const onDeckTonsText = deck.consumptionMode === ConsumptionMode.BoardFoot.value ? 'N/A' : tonStringFromPounds(deck.onDeck)
    const onDeckMbfText = deck.consumptionMode === ConsumptionMode.BoardFoot.value ? mbfStringFromBf(deck.onDeck) : 'N/A'

    let deckName = deck.name
    if (deck.isDefault) {
      deckName = `${deck.name} (Default)`
    }

    return [
      { text: deckName, border: noBorder, fontSize: bodyFontSize, alignment: 'left', fillColor: rowColor(idx) },
      { text: statusText, border: noBorder, fontSize: bodyFontSize, alignment: 'center', fillColor: rowColor(idx) },
      { text: consumptionModeText, border: noBorder, fontSize: bodyFontSize, alignment: 'center', fillColor: rowColor(idx) },
      { text: loadsText, border: noBorder, fontSize: bodyFontSize, alignment: 'right', fillColor: rowColor(idx) },
      { text: onDeckTonsText, border: noBorder, fontSize: bodyFontSize, alignment: 'right', fillColor: rowColor(idx) },
      { text: onDeckMbfText, border: noBorder, fontSize: bodyFontSize, alignment: 'right', fillColor: rowColor(idx) },
      { text: formatMoney(deck.value), border: noBorder, fontSize: bodyFontSize, alignment: 'right', fillColor: rowColor(idx) }
    ]
  })

  const totalLoads = sumBy(decks.filter(deck => deck.consumptionMode === ConsumptionMode.TicketBased.value), 'loadsOnDeck')

  const totalTons = sumBy(decks.filter(deck => deck.consumptionMode !== ConsumptionMode.BoardFoot.value), 'onDeck')
  const totalMbf = sumBy(decks.filter(deck => deck.consumptionMode === ConsumptionMode.BoardFoot.value), 'onDeck')
  const totalValue = sumBy(decks, 'value')

  const totalsRow = [
    { text: 'Totals', border: topBorder, fontSize: bodyFontSize, bold: true, alignment: 'left' },
    { text: '', border: topBorder, fontSize: bodyFontSize, bold: true },
    { text: '', border: topBorder, fontSize: bodyFontSize, bold: true },
    { text: totalLoads.toString(), border: topBorder, fontSize: bodyFontSize, bold: true, alignment: 'right' },
    { text: tonStringFromPounds(totalTons), border: topBorder, fontSize: bodyFontSize, bold: true, alignment: 'right' },
    { text: mbfStringFromBf(totalMbf), border: topBorder, fontSize: bodyFontSize, bold: true, alignment: 'right' },
    { text: formatMoney(totalValue), border: topBorder, fontSize: bodyFontSize, bold: true, alignment: 'right' }
  ]

  const body = [headers, ...rows, totalsRow]

  return {
    marginBottom: 20,
    table: {
      widths: ['*', '*', '*', 'auto', 'auto', 'auto', 'auto'],
      body,
      dontBreakRows: true,
      headerRows: 1,
      keepWithHeaderRows: true
    },
    layout: {
      hLineWidth: function (i, node) {
        return (i === 0 || i === node.table.body.length) ? 2 : 1
      },
      vLineWidth: function (i, node) {
        return (i === 0 || i === node.table.widths.length) ? 2 : 1
      },
      hLineColor: function (i, node) {
        return (i === 0 || i === 1 || i === node.table.body.length - 1 || i === node.table.body.length) ? 'black' : 'white'
      },
      vLineColor: function () {
        return 'black'
      }
    },
    style: nestedTableStyling
  }
}

function determineConsumptionModeText (consumptionMode) {
  if (consumptionMode === ConsumptionMode.TicketBased.value) {
    return 'Tickets'
  } else if (consumptionMode === ConsumptionMode.WeightBased.value) {
    return 'Weight'
  } else if (consumptionMode === ConsumptionMode.BoardFoot.value) {
    return 'Board Foot'
  } else {
    return 'Weight'
  }
}
